import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../axiosSetup";
import "bootstrap/dist/css/bootstrap.min.css";
import CustomNavbar from "../CustomNavbar";
import Select from "react-select";
import { Modal, Button } from "react-bootstrap";
import cfg from "../../config";

const getCurrentDate = () => new Date().toISOString().split("T")[0];

const customStyles = {
  control: (s) => ({ ...s, backgroundColor: "white", color: "black", borderColor: "#6c757d" }),
  singleValue: (s) => ({ ...s, color: "black" }),
  menu: (s) => ({ ...s, backgroundColor: "white" }),
  option: (s, { isFocused, isSelected }) => ({
    ...s,
    backgroundColor: isSelected ? "#007bff" : isFocused ? "#e9ecef" : "white",
    color: "black",
  }),
};

// Konvertiert das gespeicherte Notification-Objekt zurück in einen zielgruppe-Key
const deriveZielgruppe = (notification) => {
  if (!notification) return "alle";
  if (notification.forEveryone) return "alle";
  if (notification.forCouncil) return "zunftrat";
  if (notification.forCostumeType) return `kostuemtyp_${notification.forCostumeType}`;
  if (notification.member?.memberID) return "person";
  return "alle";
};

const ZRNotificationCreate = () => {
  const { state } = useLocation();
  const notification = state ? state.notification : null;
  const navigate = useNavigate();

  const [costumeTypes, setCostumeTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [formData, setFormData] = useState({
    priority: "",
    title: "",
    body: "",
    createdBy: cfg.adminRoleName,
    publishDate: getCurrentDate(),
    zielgruppe: "alle",
    memberID: "",
    sendMail: false,
  });

  // Kostümtypen + Mitglieder laden
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const headers = { Authorization: `Bearer ${token}` };

    api.get("/api/costume-types", { headers })
      .then(r => setCostumeTypes(r.data || []))
      .catch(() => {});

    api.get("/api/member-zunftrat", { headers })
      .then(r => setMembers(r.data || []))
      .catch(() => {});
  }, []);

  // Beim Bearbeiten: Formular befüllen
  useEffect(() => {
    if (!notification) return;
    const fmt = (d) => {
      if (!d) return getCurrentDate();
      if (d.includes(".")) {
        const [day, month, year] = d.split(".");
        return `${year}-${month}-${day}`;
      }
      return d.slice(0, 10);
    };
    setFormData({
      priority: notification.priority || "",
      title: notification.title || "",
      body: notification.body || "",
      createdBy: notification.createdBy || cfg.adminRoleName,
      publishDate: fmt(notification.publishDate) || getCurrentDate(),
      zielgruppe: deriveZielgruppe(notification),
      memberID: notification.member?.memberID || "",
      sendMail: false,
    });
  }, [notification]);

  const memberOptions = members.map(m => ({
    value: m.ID,
    label: `${m.firstName} ${m.lastName}`,
  }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "zielgruppe" && value !== "person" ? { memberID: "" } : {}),
    }));
  };

  // Zielgruppe → API-Felder mappen
  const buildPayload = () => {
    const { zielgruppe, memberID, ...rest } = formData;
    const base = { ...rest, forEveryone: "Nein", forZunftrat: "Nein", memberID: "", forCostumeType: null };

    if (zielgruppe === "alle") {
      base.forEveryone = "Ja";
    } else if (zielgruppe === "zunftrat") {
      base.forZunftrat = "Ja";
    } else if (zielgruppe.startsWith("kostuemtyp_")) {
      base.forCostumeType = zielgruppe.replace("kostuemtyp_", "");
    } else if (zielgruppe === "person") {
      base.memberID = memberID;
    }

    return base;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const payload = buildPayload();
      if (notification) {
        await api.put(`/api/edit-notification/${notification.ID}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-notification", { state: { message: "Mitteilung erfolgreich bearbeitet!" } });
      } else {
        await api.post("/api/create-notification", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-notification", { state: { message: "Mitteilung erfolgreich erstellt!" } });
      }
    } catch (err) {
      setError("Fehler beim Speichern: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("authToken");
      await api.delete(`/api/delete-notification/${notification.ID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowDeleteModal(false);
      navigate("/zr-notification", { state: { message: "Mitteilung erfolgreich gelöscht!" } });
    } catch (err) {
      setError("Fehler beim Löschen: " + err.message);
      setShowDeleteModal(false);
    }
  };

  return (
    <div>
      <CustomNavbar />
      <div className="container mt-4" style={{ maxWidth: 680 }}>
        <h2 className="text-center mb-4">
          {notification ? "Mitteilung bearbeiten" : "Neue Mitteilung erstellen"}
        </h2>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Priorität */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Priorität</label>
            <select className="form-select" name="priority" value={formData.priority} onChange={handleChange} required>
              <option value="">Bitte wählen…</option>
              <option value="3">Normal</option>
              <option value="2">Wichtig</option>
              <option value="1">Sehr wichtig</option>
            </select>
          </div>

          {/* Titel */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Titel</label>
            <input
              type="text"
              className="form-control"
              name="title"
              value={formData.title}
              onChange={handleChange}
              maxLength={100}
              required
            />
            <div className="form-text text-end">{formData.title.length}/100</div>
          </div>

          {/* Beschreibung */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Beschreibung</label>
            <textarea
              className="form-control"
              rows="5"
              name="body"
              value={formData.body}
              onChange={handleChange}
              maxLength={2000}
              required
            />
            <div className="form-text text-end">{(formData.body || "").length}/2000</div>
          </div>

          {/* Veröffentlichungsdatum */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Veröffentlichungsdatum</label>
            <input
              type="date"
              className="form-control"
              name="publishDate"
              value={formData.publishDate}
              onChange={handleChange}
            />
          </div>

          {/* ── Zielgruppe ── */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Zielgruppe</label>
            <select className="form-select" name="zielgruppe" value={formData.zielgruppe} onChange={handleChange}>
              <option value="alle">Alle aktiven {cfg.memberTermPlural}</option>
              <option value="zunftrat">Nur {cfg.adminRoleName}</option>
              {costumeTypes.map(ct => (
                <option key={ct.id} value={`kostuemtyp_${ct.name}`}>
                  {cfg.costumeTerm}typ: {ct.name}
                </option>
              ))}
              <option value="person">Einzelne Person</option>
            </select>
          </div>

          {/* Mitglied – nur wenn "Einzelne Person" */}
          {formData.zielgruppe === "person" && (
            <div className="mb-3">
              <label className="form-label fw-semibold">{cfg.memberTerm} auswählen</label>
              <Select
                options={memberOptions}
                styles={customStyles}
                value={memberOptions.find(o => o.value === formData.memberID) || null}
                onChange={opt => setFormData(prev => ({ ...prev, memberID: opt ? opt.value : "" }))}
                isClearable
                placeholder={`${cfg.memberTerm} wählen…`}
              />
            </div>
          )}

          {/* E-Mail */}
          <div className="form-check mb-4">
            <input
              className="form-check-input"
              type="checkbox"
              name="sendMail"
              checked={formData.sendMail}
              onChange={handleChange}
            />
            <label className="form-check-label">Zusätzlich per E-Mail versenden</label>
          </div>

          {/* Buttons */}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Speichern…" : "Speichern"}
            </button>
            {notification && (
              <button type="button" className="btn btn-danger" onClick={() => setShowDeleteModal(true)} disabled={loading}>
                Löschen
              </button>
            )}
          </div>
        </form>

        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Mitteilung löschen</Modal.Title>
          </Modal.Header>
          <Modal.Body>Möchtest du diese Mitteilung wirklich löschen?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Abbrechen</Button>
            <Button variant="danger" onClick={handleDelete}>Löschen</Button>
          </Modal.Footer>
        </Modal>
      </div>
      <br /><br />
    </div>
  );
};

export default ZRNotificationCreate;
